import { ChangeEvent, DragEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { downloadCsv } from './downloadCsv'
import { BULK_REJECT_MAX_CSV_SIZE_BYTES, BULK_REJECT_MAX_ROWS, buildBulkRejectTemplateCsv } from './parseBulkRejectCsv'
import { ActionModal, Alert, ButtonText, Dropzone, Spacer, theme } from '@percent/lemonade'

const TEMPLATE_CSV_FILENAME = 'bulk-reject-template.csv'

// Spelled out rather than via Mantine's `MIME_TYPES`, which apps must not import directly.
const ACCEPTED_FILE_TYPES = ['text/csv']

type BulkRejectUploadViewProps = {
  selectedFiles: File[]
  canReview: boolean
  onSelectFiles: (files: File[]) => void
  onFileInputChange: (event: ChangeEvent<HTMLDivElement>) => void
  onDrop: (event: DragEvent<HTMLDivElement>) => void
  onClose: () => void
  onReview: () => void
}

export function BulkRejectUploadView({
  selectedFiles,
  canReview,
  onSelectFiles,
  onFileInputChange,
  onDrop,
  onClose,
  onReview
}: Readonly<BulkRejectUploadViewProps>) {
  const { t } = useTranslation()

  return (
    <ActionModal
      title={t('dialog.avBulkReject.title')}
      primaryButtonText={t('button.continue')}
      primaryBtnTestId="av-bulk-reject-review-button"
      secondaryButtonText={t('button.cancel')}
      handleClose={onClose}
      handleSubmit={onReview}
      disabled={!canReview}
    >
      <Alert variant="info">
        <strong>{t('dialog.avBulkReject.alert.title')}</strong>
        <div>{t('dialog.avBulkReject.alert.description', { maxRows: BULK_REJECT_MAX_ROWS })}</div>
        <ul style={{ paddingLeft: theme.spacing.m }}>
          <li>
            <Trans
              i18nKey="dialog.avBulkReject.alert.columns.agentVerificationId"
              components={{ b: <strong />, i: <i /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="dialog.avBulkReject.alert.columns.rejectionReasonCode"
              components={{ b: <strong />, i: <i /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="dialog.avBulkReject.alert.columns.fraudulentFlags"
              components={{ b: <strong />, i: <i /> }}
            />
          </li>
        </ul>
        <ButtonText variant="primary" onClick={() => downloadCsv(buildBulkRejectTemplateCsv(), TEMPLATE_CSV_FILENAME)}>
          {t('dialog.avBulkReject.alert.downloadTemplate')}
        </ButtonText>
      </Alert>
      <Spacer size={4} />
      <div data-testid="av-bulk-reject-dropzone-container" onChangeCapture={onFileInputChange} onDropCapture={onDrop}>
        <Dropzone
          title={t('dialog.avBulkReject.dropzoneTitle')}
          description={t('dialog.avBulkReject.dropzoneDescription')}
          files={selectedFiles}
          maxFiles={1}
          onChange={onSelectFiles}
          accept={ACCEPTED_FILE_TYPES}
          maxSize={BULK_REJECT_MAX_CSV_SIZE_BYTES}
          testId="av-bulk-reject-dropzone"
        />
      </div>
    </ActionModal>
  )
}
